import isEqual from 'lodash/isEqual';
import type { EditorState } from '../../vendor/src/features/remotion-editor/editor/state/types';
import { companyVoiceOwner } from '../../protocol/voice-owner';
import { readCompanyVoiceClip } from '../../protocol/voice-replacement';
import { resolveGroupGeometry } from '../../vendor/src/features/remotion-editor/editor/items/group/group-geometry';
import { trimClipLeft } from '../../vendor/src/features/remotion-editor/editor/state/actions/trim-clip-left';
import { trimClipRight } from '../../vendor/src/features/remotion-editor/editor/state/actions/trim-clip-right';
import { hasPendingTimelineEdit } from './edit-ownership';
export type GroupVoiceWindowPlan = {
    kind: 'none';
} | {
    kind: 'blocked';
    reason: string;
} | {
    kind: 'ready';
    state: EditorState;
    clipId: string;
    filename: string;
    from: number;
    to: number;
    oldFrom: number;
    oldTo: number;
    fps: number;
};
const valid = (from: number, duration: number) => Number.isSafeInteger(from) && from >= 0 && Number.isSafeInteger(duration) && duration > 0 && Number.isSafeInteger(from + duration);
/** Explicitly trim only invisible audio tails; never normalize group caches. */
export function planGroupVoiceWindow(state: EditorState, id: string): GroupVoiceWindowPlan {
    const blocked = (reason: string): GroupVoiceWindowPlan => ({ kind: 'blocked', reason });
    const before = state.undoableState, item = before.items[id];
    if (item?.type !== 'audio')
        return { kind: 'none' };
    if (!valid(item.from, item.durationInFrames) || !Number.isFinite(before.fps) || before.fps <= 0)
        return blocked('음성의 시간 구간을 확인해 주세요.');
    let owner: ReturnType<typeof companyVoiceOwner>;
    try {
        owner = companyVoiceOwner(before, id);
    }
    catch {
        return blocked('그룹 연결을 확인하거나 병합을 해제해 주세요.');
    }
    let from = item.from, to = item.from + item.durationInFrames;
    for (const key of owner.parentIds) {
        const parent = before.items[key];
        if (parent?.type !== 'group')
            return blocked('부모 그룹을 확인해 주세요.');
        if (parent.preserveGroupFrame) {
            if (!valid(parent.from, parent.durationInFrames))
                return blocked('부모 그룹의 표시 구간을 확인해 주세요.');
            from = Math.max(from, parent.from);
            to = Math.min(to, parent.from + parent.durationInFrames);
        }
    }
    if (from === item.from && to === item.from + item.durationInFrames)
        return { kind: 'none' };
    if (to <= from)
        return blocked('그룹 안에 남은 음성 구간이 없습니다. 부모 그룹의 자르기를 조정해 주세요.');
    const asset = before.assets[item.assetId];
    const sourceEnd = item.audioStartFromInSeconds + item.durationInFrames / before.fps * item.playbackRate;
    if (asset?.type !== 'audio' || asset.id !== item.assetId || !Number.isFinite(asset.durationInSeconds) || asset.durationInSeconds <= 0
        || !Number.isFinite(item.audioStartFromInSeconds) || item.audioStartFromInSeconds < 0
        || !Number.isFinite(item.playbackRate) || item.playbackRate <= 0 || !Number.isFinite(sourceEnd)
        || sourceEnd > asset.durationInSeconds + item.playbackRate / before.fps)
        return blocked('원본 음성의 재생 범위를 확인해 주세요.');
    if (owner.track.locked)
        return blocked('음성 트랙의 잠금을 풀어 주세요.');
    if (hasPendingTimelineEdit(state))
        return blocked('진행 중인 편집을 먼저 마쳐 주세요.');
    if (Object.values(before.transitions).some(t => t.sourceItemId === id || t.targetItemId === id))
        return blocked('연결된 전환을 조정한 뒤 음성 구간을 맞춰 주세요.');
    let next = state;
    if (from > item.from)
        next = trimClipLeft({ state: next, itemId: id, framePosition: from });
    if (to < item.from + item.durationInFrames)
        next = trimClipRight({ state: next, itemId: id, framePosition: to });
    const trimmed = next.undoableState.items[id];
    if (trimmed?.type !== 'audio' || trimmed.from !== from || trimmed.durationInFrames !== to - from)
        return blocked('음성의 자르기 범위를 확인해 주세요.');
    for (const key of owner.parentIds) {
        const group = before.items[key];
        if (group?.type !== 'group')
            return blocked('부모 그룹을 확인해 주세요.');
        const old = resolveGroupGeometry(group, before.items), now = resolveGroupGeometry(group, next.undoableState.items);
        if (old.from !== now.from || old.durationInFrames !== now.durationInFrames)
            return blocked('중간 그룹의 구간이 달라집니다. 부모 자르기를 조정하거나 병합을 해제한 뒤 편집해 주세요.');
    }
    try {
        readCompanyVoiceClip(next.undoableState, id);
    }
    catch {
        return blocked('원본 음성 범위나 부모 연결을 확인해 주세요.');
    }
    return { kind: 'ready', clipId: id, filename: asset?.filename ?? '선택 음성', from, to, oldFrom: item.from, oldTo: item.from + item.durationInFrames, fps: before.fps,
        state: { ...next, selectedItems: [id], activeGroupId: owner.parentIds[0] ?? null, inspectedGroupMemberId: null, timelineTimeEditApplied: true } };
}
export function isGroupVoiceWindowUpdate(previous: EditorState, next: EditorState, id: string): boolean {
    const before = previous.undoableState, after = next.undoableState;
    if (before.assets !== after.assets || before.fps !== after.fps)
        return false;
    const plan = planGroupVoiceWindow(previous, id);
    if (plan.kind !== 'ready')
        return false;
    if (Object.entries(before.items).some(([key, value]) => key !== id && after.items[key] !== value))
        return false;
    return isEqual(plan.state.undoableState.items[id], after.items[id]);
}
